import { Badge } from './ui/badge';
import { SoftCard } from './ui/modern-card';
import { Button } from './ui/button';
import { MapPin, Clock, CheckCircle, AlertCircle, RefreshCw, Info } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { Report } from '../types';

interface ReportsListProps {
  reports: Report[];
  onReportClick: (report: Report) => void;
  selectedReportId?: string;
  onViewDetail?: (reportOrId: Report | string) => void;
  onUpdateStatus?: (reportId: string, status: 'in_progress' | 'done') => void;
  onRefresh?: () => void;
}

export default function ReportsList({ reports, onReportClick, selectedReportId, onViewDetail, onUpdateStatus, onRefresh }: ReportsListProps) {
  const { user, isAdmin, isZoneManager } = useAuth();
  
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Filtrer les signalements selon le rôle
  const filteredReports = (isAdmin || isZoneManager) ? reports : reports.filter(r => r.status === 'in_progress');
  const reportsEnCours = filteredReports.filter(r => r.status === 'in_progress');  
  const reportsTermines = filteredReports.filter(r => r.status === 'done');

  const getStatusBadge = (status: string) => {
    if (status === 'done') {
      return (
        <Badge variant="secondary" className="bg-green-100 text-green-800">
          <CheckCircle className="w-3 h-3 mr-1" />
          Terminé
        </Badge>
      );
    }
    return (
      <Badge variant="destructive" className="bg-orange-100 text-orange-800">
        <AlertCircle className="w-3 h-3 mr-1" />
        En cours
      </Badge>
    );
  };

  const renderReportCard = (report: Report) => (
    <SoftCard
      key={report.id}
      className={`p-4 cursor-pointer transition-all duration-200 hover:shadow-md ${
        selectedReportId === report.id 
          ? 'ring-2 ring-blue-500/50 shadow-md bg-blue-50/50' 
          : 'hover:shadow-md hover:bg-white/90'
      }`}
      onClick={() => {
        onReportClick(report);
      }}
    >
      <div className="space-y-2">
        {/* Date et statut */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Clock className="w-3 h-3 text-muted-foreground" />
            <span className="text-xs text-muted-foreground">
              {formatDate(report.date)}
            </span>
          </div>
          {getStatusBadge(report.status || 'in_progress')}
        </div>

        {/* Description */}
        {report.description && (
          <p className="text-sm text-muted-foreground line-clamp-3">
            {report.description}
          </p>
        )}

        {/* Position GPS */}
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <MapPin className="w-4 h-4" />
          <span className="font-mono text-xs">
            {report.latitude.toFixed(6)}, {report.longitude.toFixed(6)}
          </span>
        </div>

        {/* Propriétaire */}
        {report.userId === user?.id && (
          <div className="text-xs pt-1">
            <span className="text-blue-600 font-medium">Signalement créé par vous</span>
          </div>
        )}
        
        {/* Actions */}
        <div className="flex justify-end gap-2 pt-1">
          {onViewDetail && (
            <Button
              size="sm"
              variant="outline"
              onClick={(e) => {
                e.stopPropagation();
                onViewDetail(report.id);
              }}
              className="h-7 px-2 text-xs border-gray-300 hover:bg-gray-50"
            >
              <Info className="w-3 h-3 mr-1" />
              Détails
            </Button>
          )}
          
          {/* Bouton terminer/rouvrir pour admin et gestionnaires */}
          {(isAdmin || isZoneManager) && onUpdateStatus && (
            <>
              {report.status === 'in_progress' ? (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={(e) => {
                    e.stopPropagation();
                    onUpdateStatus(report.id, 'done');
                  }}
                  className="h-7 px-2 text-xs text-green-600 border-green-300 hover:bg-green-50"
                >
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Terminer
                </Button>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={(e) => {
                    e.stopPropagation();
                    onUpdateStatus(report.id, 'in_progress');
                  }}
                  className="h-7 px-2 text-xs text-orange-600 border-orange-300 hover:bg-orange-50"
                >
                  <AlertCircle className="w-3 h-3 mr-1" />
                  Rouvrir
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </SoftCard>
  );

  return (
    <div className="h-full overflow-hidden flex flex-col">
      {/* Header */}
      <div className="p-4 bg-gray-50/30 border-b border-gray-100/50">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              {(isAdmin || isZoneManager) ? 'Tous les signalements' : 'Signalements en cours'}
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              {(isAdmin || isZoneManager)
                ? `${reportsEnCours.length} en cours • ${reportsTermines.length} terminés`
                : `${reportsEnCours.length} signalement${reportsEnCours.length > 1 ? 's' : ''}`
              }
            </p>
          </div>
          {onRefresh && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onRefresh}
              className="gap-2 hover:bg-gray-100"
              title="Recharger les signalements"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>
      
      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {filteredReports.length === 0 ? (
          <div className="p-4 text-center text-muted-foreground">
            <AlertCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p>Aucun signalement</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Section Signalements en cours */}
            {reportsEnCours.length > 0 && (
              <div>
                {(isAdmin || isZoneManager) && (
                  <div className="px-4 py-2 bg-orange-50/50 border-b border-orange-100">
                    <h3 className="font-medium text-orange-800 text-sm">
                      En cours ({reportsEnCours.length})
                    </h3>
                  </div>
                )}
                <div className="p-4 space-y-3">
                  {reportsEnCours.map(renderReportCard)}
                </div>
              </div>
            )}

            {/* Section Signalements terminés (admin et zoneManager) */}
            {(isAdmin || isZoneManager) && reportsTermines.length > 0 && (
              <div>
                <div className="px-4 py-2 bg-green-50/50 border-b border-green-100">
                  <h3 className="font-medium text-green-800 text-sm">
                    Terminés ({reportsTermines.length})
                  </h3>
                </div>
                <div className="p-4 space-y-3 pb-20">
                  {reportsTermines.map(renderReportCard)}
                </div>
              </div>
            )}

            {/* Padding bottom pour éviter que le bouton cache le contenu */}
            {!isAdmin && !isZoneManager && (
              <div className="pb-20" />
            )}
          </div>
        )}
      </div>
      
      {/* Légende */}
      <div className="border-t border-gray-100/50 p-4 pb-20 bg-gray-50/30">
        <div className="text-xs text-muted-foreground space-y-1">
          <p>• Cliquez pour centrer la carte sur le signalement</p>
          <p>• Bouton "Détails" pour voir toutes les informations</p>
          {(isAdmin || isZoneManager) && <p>• Actions rapides pour changer le statut</p>}
        </div>
      </div>
    </div>
  );
}